import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
/**
 * Mock de productos para testing y desarrollo
 * Simula la respuesta de GET /api/products/by-slug?slug={slug}&country={country}
 */

export interface MockProduct {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_usd: number;
  price_ars?: number;
  recommended_price: number;
  recommended_currency: string;
  image_url?: string;
  image_alt?: string;
  hero_image_url?: string;
  thumbnail_url?: string;
  gallery_images?: string[];
  video_url?: string;
  cta_button_text?: string;
  cta_button_color?: string;
  show_quick_buy?: boolean;
  page_title?: string;
  page_description?: string;
  product_type: string;
  product_category: string;
  is_active: boolean;
  visibility: string;
}

export interface MockProductApiResponse {
  product: MockProduct;
  url: string;
}

/** Producto con video (Mux Playback ID) */
export const mockProductWithVideo: MockProduct = {
  id: "mock-uuid-video-001",
  name: "Video Personalizado",
  slug: "video-personalizado",
  description: `Un video único y personalizado de ${BRAND_PERSON_NAME} en 48 horas`,
  price_usd: 50,
  price_ars: 10000,
  recommended_price: 10000,
  recommended_currency: "ARS",
  video_url: "2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs", // Mux Playback ID (la API devuelve solo esto)
  image_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=720",
  hero_image_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=800",
  thumbnail_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=400",
  cta_button_text: "Comprar Video",
  cta_button_color: "#ff6b6b",
  show_quick_buy: true,
  page_title: `Video Personalizado | ${BRAND_PERSON_NAME}`,
  page_description: "Recibe un video único en 48 horas. Perfecto para regalos.",
  product_type: "one-time",
  product_category: "individual",
  is_active: true,
  visibility: "public",
};

/** Producto solo con imágenes (sin video) */
export const mockProductImagesOnly: MockProduct = {
  id: "mock-uuid-images-002",
  name: "Pack de Fotos Premium",
  slug: "pack-fotos-premium",
  description: "50+ fotos exclusivas en alta resolución. Contenido único que no encontrarás en ningún otro lugar.",
  price_usd: 29.99,
  price_ars: 5000,
  recommended_price: 5000,
  recommended_currency: "ARS",
  image_url: "https://via.placeholder.com/400x600/ec4899/ffffff?text=Pack+Fotos",
  hero_image_url: "https://via.placeholder.com/800x600/ec4899/ffffff?text=Pack+Fotos+Hero",
  thumbnail_url: "https://via.placeholder.com/200x200/ec4899/ffffff?text=Thumb",
  gallery_images: [
    "https://via.placeholder.com/400x600/8b5cf6/ffffff?text=Foto+1",
    "https://via.placeholder.com/400x600/8b5cf6/ffffff?text=Foto+2",
  ],
  cta_button_text: "Desbloquear Pack",
  cta_button_color: "#8b5cf6",
  show_quick_buy: true,
  page_title: `Pack Fotos Premium | ${BRAND_PERSON_NAME}`,
  page_description: "50+ fotos exclusivas en alta resolución",
  product_type: "one-time",
  product_category: "digital",
  is_active: true,
  visibility: "public",
};

/** Producto con precio USD (para países no-AR) */
export const mockProductUSD: MockProduct = {
  ...mockProductWithVideo,
  id: "mock-uuid-usd-003",
  slug: "video-personalizado-usd",
  recommended_price: 50,
  recommended_currency: "USD",
};

/** Producto estático solo USD — simula respuesta by-slug para /p/mock-usd */
export const mockProductStaticUsd: MockProduct = {
  id: "mock-static-usd-001",
  name: "Video Personalizado",
  slug: "mock-usd",
  description: `Un video único y personalizado de ${BRAND_PERSON_NAME} en 48 horas. Producto de ejemplo en USD.`,
  price_usd: 49.99,
  price_ars: undefined,
  recommended_price: 49.99,
  recommended_currency: "USD",
  image_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=720",
  hero_image_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=800",
  cta_button_text: "Comprar",
  page_title: `Video Personalizado | ${BRAND_PERSON_NAME}`,
  page_description: "Recibe un video único en 48 horas. Perfecto para regalos.",
  product_type: "one-time",
  product_category: "individual",
  is_active: true,
  visibility: "public",
};

/** Producto estático solo ARS — simula respuesta by-slug para /p/mock-ars */
export const mockProductStaticArs: MockProduct = {
  id: "mock-static-ars-001",
  name: "Video Personalizado",
  slug: "mock-ars",
  description: `Un video único y personalizado de ${BRAND_PERSON_NAME} en 48 horas. Producto de ejemplo en ARS.`,
  price_usd: 0,
  price_ars: 15000,
  recommended_price: 15000,
  recommended_currency: "ARS",
  image_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=720",
  hero_image_url: "https://image.mux.com/2YNbonczFts4lEfrbs7ppTd2IgU9XCyuR00NT0175jmrs/thumbnail.jpg?time=0&width=800",
  cta_button_text: "Comprar",
  page_title: `Video Personalizado | ${BRAND_PERSON_NAME}`,
  page_description: "Recibe un video único en 48 horas. Perfecto para regalos.",
  product_type: "one-time",
  product_category: "individual",
  is_active: true,
  visibility: "public",
};

/** Mapa de slugs a productos mock */
const mockProductsBySlug: Record<string, MockProduct> = {
  "video-personalizado": mockProductWithVideo,
  "pack-fotos-premium": mockProductImagesOnly,
  "video-personalizado-usd": mockProductUSD,
  "mock-usd": mockProductStaticUsd,
  "mock-ars": mockProductStaticArs,
};

/**
 * Obtiene el producto mock con el precio recomendado según el país
 */
export function getMockProductBySlug(
  slug: string,
  country: string = "AR"
): MockProductApiResponse | null {
  const product = mockProductsBySlug[slug];
  if (!product) return null;

  const isArgentina = country === "AR";
  const recommendedPrice = isArgentina && product.price_ars != null
    ? product.price_ars
    : product.price_usd;
  const recommendedCurrency = isArgentina && product.price_ars != null ? "ARS" : "USD";

  return {
    product: {
      ...product,
      recommended_price: recommendedPrice,
      recommended_currency: recommendedCurrency,
    },
    url: `/gift/${product.slug}`,
  };
}

/**
 * Mock de fetch para usar en tests.
 * Intercepta llamadas a la API de productos
 *
 * Uso en Vitest:
 *   vi.stubGlobal('fetch', mockProductFetch);
 *
 * Uso en desarrollo (en main.tsx o ProductReel):
 *   if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_PRODUCTS) {
 *     const originalFetch = window.fetch;
 *     window.fetch = (url, opts) => {
 *       if (typeof url === 'string' && url.includes('/api/products/by-slug')) {
 *         const slug = new URL(url).searchParams.get('slug');
 *         const country = new URL(url).searchParams.get('country') || 'AR';
 *         const data = getMockProductBySlug(slug || '', country);
 *         return data ? Promise.resolve({ ok: true, json: () => Promise.resolve(data) }) : originalFetch(url, opts);
 *       }
 *       return originalFetch(url, opts);
 *     };
 *   }
 */
export async function mockProductFetch(
  input: RequestInfo | URL,
  _init?: RequestInit
): Promise<Response> {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;

  if (!url.includes("/api/products/by-slug")) {
    return fetch(input, _init);
  }

  try {
    const urlObj = new URL(url);
    const slug = urlObj.searchParams.get("slug") || "";
    const country = urlObj.searchParams.get("country") || "AR";

    const data = getMockProductBySlug(slug, country);

    if (data) {
      return new Response(JSON.stringify(data), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }
  } catch {
    // Fall through to real fetch
  }

  return fetch(input, _init);
}
